import { useEffect } from "react";
import { CotationFormData } from "@/types/forms/home/cotationFormData.form";
import { useFormContext, UseFormTrigger, useWatch } from "react-hook-form";
import CotationCard from "./CotationCard";
import { volumesOptions, volumesOptionsWoodBox } from "@/constants/options/volumes.option";
import StyledSwitch from "@/components/ui/customs/StyledSwitch";
import { useGetWoodboxPriceDefault } from "@/api/woodboxPrice.api";
import { GetWoodboxPriceResponse } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import { ControlledSelectV2 } from "@/components/ui/customs/controlled-select-v2";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import TrashIcon from "@/assets/icons/trash.svg?react";
import { Package } from "lucide-react";

type WoodBoxesCardProps = {
  defaultBottleQuantity: number;
  woodboxDefault: number;
  hasInitValue: boolean;
};

const WoodBoxesCard = ({
  defaultBottleQuantity,
  woodboxDefault,
  hasInitValue,
}: WoodBoxesCardProps) => {
  const { data: woodboxPriceDefault } = useGetWoodboxPriceDefault();

  const {
    control,
    watch,
    setValue,
    trigger,
    formState: { dirtyFields },
  } = useFormContext<CotationFormData>();

  const products = watch("products");
  const woodBoxes = watch("woodBoxes");
  const isProductsDirty = dirtyFields.products?.some(
    (product) => product && (product.volume || product.bottleQuantity || product.value),
  );

  useEffect(() => {
    // If there are initial values and products haven't been modified yet, don't recalculate
    if (hasInitValue && !isProductsDirty) {
      return;
    }

    const productsMergedByVolume = products
      .reduce(
        (acc, product) => {
          if (!product.volume || !product.bottleQuantity || !product.value) return acc;
          if (product.volume && acc.some((el) => el.volume === product.volume)) {
            const index = acc.findIndex((el) => el.volume === product.volume);
            acc[index].bottleQuantity += Number(product.bottleQuantity) || 0;
          } else {
            acc.push({
              volume: product.volume,
              bottleQuantity: Number(product.bottleQuantity) || 0,
              value: product.value,
            });
          }
          return acc;
        },
        [] as CotationFormData["products"],
      )
      .filter((product) => product.volume <= 9 && product.volume !== 4.5);

    const woodboxArray = [
      ...productsMergedByVolume.map((product) => {
        if (product.volume === 0.75) {
          if (product.bottleQuantity < defaultBottleQuantity) {
            const boxSizes = [1, 2, 3, 6];
            const boxSize = boxSizes.reverse().find((size) => size <= product.bottleQuantity) ?? 1;
            return {
              bottleQuantity: boxSize,
              boxQuantity: Math.floor(product.bottleQuantity / boxSize),
              bottleVolume: product.volume,
            };
          }
          return {
            bottleQuantity: woodboxDefault,
            boxQuantity: Math.floor(product.bottleQuantity / defaultBottleQuantity),
            bottleVolume: product.volume,
          };
        } else if (product.volume === 1.5) {
          const boxSizes = [1, 2, 3, 6];
          const boxSize = boxSizes.reverse().find((size) => size <= product.bottleQuantity) ?? 1;
          return {
            bottleQuantity: boxSize,
            boxQuantity: Math.floor(product.bottleQuantity / boxSize),
            bottleVolume: product.volume,
          };
        } else {
          return {
            bottleQuantity: 1,
            boxQuantity: Math.floor(product.bottleQuantity / 1),
            bottleVolume: product.volume,
          };
        }
      }),
    ];

    setValue("woodBoxes", woodboxArray);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(products), setValue, isProductsDirty]);

  const handleAppendWoodbox = async () => {
    const woodboxError = await trigger("woodBoxes");

    if (!woodboxError) return;

    setValue("woodBoxes", [
      ...woodBoxes,
      {
        bottleVolume: volumesOptions[0].value,
        bottleQuantity: "" as unknown as number,
        boxQuantity: "" as unknown as number,
      },
    ]);
  };

  const handleRemoveWoodbox = (index: number) => {
    setValue(
      "woodBoxes",
      woodBoxes.filter((_, i) => i !== index),
    );
  };

  return (
    <CotationCard>
      <div className="flex w-full flex-1 flex-col gap-4">
        <div className="flex flex-1 flex-row gap-4">
          <Package className="size-8 fill-zinc-900 stroke-1 text-white" />
          <div className="flex flex-1 flex-col">
            <p className="font-semibold">Option caisse bois</p>
          </div>
          <StyledSwitch name="woodBoxesActivated" control={control} />
        </div>
        {woodBoxes.map((item, index) => {
          return (
            <WoodboxItem
              key={`woodbox-${index}`}
              control={control}
              index={index}
              item={item}
              remove={handleRemoveWoodbox}
              setValue={setValue}
              trigger={trigger}
              woodboxPrice={woodboxPriceDefault?.filter((e) => e.region === "FRANCE") || []}
            />
          );
        })}
        <Button
          variant="tertiary"
          onClick={handleAppendWoodbox}
          className="h-auto self-end! text-wrap whitespace-normal"
        >
          Ajouter une caisse bois supplémentaire
        </Button>
      </div>
    </CotationCard>
  );
};

type WoodboxItemProps = {
  index: number;
  control: any;
  trigger: UseFormTrigger<CotationFormData>;
  remove: (index: number) => void;
  setValue: any;
  item: any;
  woodboxPrice: GetWoodboxPriceResponse;
};

const WoodboxItem = ({
  index,
  control,
  item,
  remove,
  setValue,
  trigger,
  woodboxPrice,
}: WoodboxItemProps) => {
  const selectedVolume = useWatch({
    control,
    name: `woodBoxes.${index}.bottleVolume`,
  });

  const getBottleQuantityOptions = (volume: number) => {
    const volumeData = woodboxPrice.filter((v) => v.bottleVolume === volume);
    if (!volumeData) return [];
    return volumeData
      .sort((a, b) => a.bottleQuantity - b.bottleQuantity)
      .map(({ bottleQuantity }) => ({
        label: bottleQuantity.toString(),
        value: bottleQuantity,
      }));
  };

  const bottleQuantityOptions = getBottleQuantityOptions(selectedVolume);

  return (
    <div className="flex flex-1 flex-col items-start gap-2 rounded-lg border-2 border-gray-300 bg-gray-50 p-4 md:flex-row">
      <div className="flex w-full flex-1 flex-col gap-2 sm:flex-row">
        <ControlledSelectV2
          label="Volume (L)"
          setValue={(value: string) => {
            setValue(`woodBoxes.${index}.bottleVolume`, Number(value));
            setValue(`woodBoxes.${index}.bottleQuantity`, "" as unknown as number);
          }}
          options={volumesOptionsWoodBox.map((opt) => ({
            label: opt.label,
            value: opt.value.toString(),
          }))}
          control={control}
          name={`woodBoxes.${index}.bottleVolume`}
          wrapperClassName="w-full"
        />
        <ControlledSelectV2
          options={bottleQuantityOptions.map((opt) => ({
            label: opt.label,
            value: opt.value.toString(),
          }))}
          setValue={(value: string) => {
            setValue(`woodBoxes.${index}.bottleQuantity`, Number(value));
            trigger(`woodBoxes.${index}.bottleQuantity`);
          }}
          label="Nb de btls"
          control={control}
          name={`woodBoxes.${index}.bottleQuantity`}
          wrapperClassName="w-full"
          placeholder=""
        />
      </div>
      <div className="flex w-full flex-1 flex-row gap-2">
        <ControlledUnifiedInput
          key={item.bottleQuantity}
          label="Nb de caisses"
          control={control}
          name={`woodBoxes.${index}.boxQuantity`}
          inputType="integer"
          wrapperClassName="w-full"
        />
        <div className="flex flex-1 flex-row items-center gap-2">
          <button
            onClick={() => remove(index)}
            className="flex h-fit items-center justify-center rounded-full p-2 hover:bg-gray-200"
          >
            <TrashIcon className="text-red-600" fill="red" width={20} height={20} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WoodBoxesCard;
